import { computed, type Ref } from 'vue'
import { DateTime } from 'luxon'
import type {
  OccasionFeedItem,
  OccasionScheduleItem
} from '#root/types/occasions'
import { priceFmt } from './utils'

export function useOccasion(
  occasion: Ref<OccasionFeedItem | null | undefined>
) {
  const description = computed(() => {
    const occ = occasion.value
    if (!occ) return undefined
    if (occ.description) return occ.description
    const vnt = occ.product_variant
    if (
      vnt &&
      typeof vnt === 'object' &&
      'description' in vnt &&
      vnt.description
    )
      return vnt.description
    const p = vnt && typeof vnt === 'object' ? vnt.product : undefined
    if (p && typeof p === 'object' && 'description' in p && p.description)
      return p.description
    return undefined
  })

  const floorPriceStr = computed(() => {
    return !occasion.value ? '' : priceFmt(occasion.value.floor_price_usd)
  })

  const image = computed(() => {
    const occ = occasion.value
    if (!occ || typeof occ !== 'object') return undefined
    const vnt = occ.product_variant
    if (!vnt || typeof vnt !== 'object') return undefined
    const vntImg = vnt as {
      list_image?: string | unknown
      person?: { list_image?: string | unknown }
      product?: { list_image?: string | unknown }
    }
    if (vntImg.list_image) return vntImg.list_image
    if (
      vntImg.person &&
      typeof vntImg.person === 'object' &&
      vntImg.person.list_image
    )
      return vntImg.person.list_image
    if (
      vntImg.product &&
      typeof vntImg.product === 'object' &&
      vntImg.product.list_image
    )
      return vntImg.product.list_image
    return undefined
  })

  const listPriceStr = computed(() => {
    return !occasion.value ? '' : priceFmt(occasion.value.list_price_usd)
  })

  const name = computed(() => {
    const occ = occasion.value
    if (!occ) return undefined
    if (occ.name) return occ.name
    const vnt = occ.product_variant
    if (vnt && typeof vnt === 'object' && 'name' in vnt && vnt.name)
      return vnt.name
    const p = vnt && typeof vnt === 'object' ? vnt.product : undefined
    if (p && typeof p === 'object' && 'name' in p && p.name) return p.name
    return undefined
  })

  const startDateTime = computed(() => {
    const occ = occasion.value
    return (
      occ &&
      occ.starts_at &&
      DateTime.fromISO(occ.starts_at, {
        zone: occ.timezone as string | undefined
      }).toLocal()
    )
  })
  const startDateStr = computed(() => {
    return (
      startDateTime.value &&
      startDateTime.value.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
    )
  })
  const endDateTime = computed(() => {
    const occ = occasion.value
    return (
      occ &&
      occ.ends_at &&
      DateTime.fromISO(occ.ends_at, {
        zone: occ.timezone as string | undefined
      }).toLocal()
    )
  })
  const endDateStr = computed(() => {
    return (
      endDateTime.value &&
      endDateTime.value.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)
    )
  })
  const timeStr = computed(() => {
    let str = ''

    if (startDateTime.value && endDateTime.value) {
      const startTime = startDateTime.value.toLocaleString(DateTime.TIME_SIMPLE)
      const endTime = endDateTime.value.toLocaleString(DateTime.TIME_SIMPLE)

      str = `${startTime} – ${endTime} (${endDateTime.value.offsetNameShort})`
    }

    return str
  })

  function scheduleTimeStr(item: OccasionScheduleItem): string {
    let str = ''
    const occ = occasion.value

    if (occ && item?.date_time != null && item.hours != null) {
      const start = DateTime.fromISO(item.date_time, {
        zone: occ.timezone as string | undefined
      }).toLocal()
      const endDt = endDateTime.value
      const offsetStr = DateTime.isDateTime(endDt)
        ? `(${endDt.offsetNameShort})`
        : ''

      str = `${start.toLocaleString(
        DateTime.DATE_MED_WITH_WEEKDAY
      )} | ${start.toLocaleString(DateTime.TIME_SIMPLE)} - ${start
        .plus({ hours: item.hours })
        .toLocaleString(DateTime.TIME_SIMPLE)} ${offsetStr}`
    }

    return str
  }

  return {
    description,
    floorPriceStr,
    image,
    listPriceStr,
    name,
    startDateStr,
    startDateTime,
    endDateStr,
    endDateTime,
    scheduleTimeStr,
    timeStr
  }
}
